import Immutable from "immutable";
import { addSpecialKeysToObj, removeSpecialKeysFromObj } from "services/key-converter";
import { uuid } from "services/generate-uuid";

export function fromAPI(message) {
  let transformedMessage;

  switch (message.get("type")) {
    case "http_request_recipe":
      transformedMessage = HTTPMessageFromAPI(message);
      break;
    case "shuffle":
      transformedMessage = shuffleFromAPI(message);
      break;
    case "widget":
      transformedMessage = widgetFromAPI(message);
      break;
    default:
      transformedMessage = message;
  }

  return transformedMessage;
}

export function getHeadersList(message) {
  const headers = message.get("headers");

  return headers
    .entrySeq()
    .map(([key, value]) => Immutable.Map({ key, value }))
    .toList();
}

export function getInputsDataList(message) {
  const inputsData = message.get("inputsData");

  return inputsData
    .entrySeq()
    .map(([key, value]) => Immutable.Map({ key, value }))
    .toList();
}

/**
 * Add a unique id to the shuffle sub-messages so we can use it as a React key
 *
 * @param {Object} message - shuffle message whose submessages an id will be added
 */
export function shuffleFromAPI(message) {
  return message.update("messages", messages => messages.map(m => m.merge({ id: uuid() })));
}

export function HTTPMessageFromAPI(message) {
  let requestPayload = message.get("requestPayload");
  const headersList = getHeadersList(message);
  const isXML = message.get("requestPayloadType") === "data";

  if (!isXML) {
    requestPayload = requestPayload.length
      ? Immutable.fromJS(removeSpecialKeysFromObj(JSON.parse(requestPayload)))
      : Immutable.List();

    requestPayload = convertMapToListDeep(requestPayload);
  }

  return message.merge({
    requestPayload,
    headersList
  });
}

export function widgetFromAPI(message) {
  const inputsDataList = getInputsDataList(message);

  return message.merge({ inputsDataList });
}

/**
 * @param {Immutable.List<unknown>} collection
 * @param {string} type
 * @returns {Immutable.List<unknown>}
 */
export function convertListToMapDeeply(collection, type = "dict") {
  let value;

  let newCollection = collection;

  if (Immutable.isIndexed(newCollection)) {
    const pairs = newCollection.map((item) => {
      value = { type: item.get("type"), value: item.get("value") };

      if (item.get("type") === "dict" || item.get("type") === "list") {
        value = { type: item.get("type"), value: convertListToMapDeeply(item.get("value"), item.get("type")) };
      }

      return type === "dict" ? [item.get("key"), value] : value;
    });
    newCollection = type === "dict" ? Immutable.Map(pairs) : Immutable.List(pairs);
  }

  newCollection = type === "dict"
    ? newCollection.filter((v, key) => key.length)
    : newCollection.filter(Boolean);

  return newCollection;
}

/**
 * @param {Immutable.List<unknown>} collection
 * @param {string} parent
 * @returns {Immutable.List<unknown>}
 */
export function convertMapToListDeep(collection, parent = "dict") {
  let type;

  return collection
    .entrySeq()
    .map(([key, value]) => {
      let newValue = value;
      type = value.get("type");

      if (["list", "dict"].includes(type)) {
        newValue = newValue.set(
          "value",
          convertMapToListDeep(newValue.get("value"), type)
        );
      }


      return parent === "dict"
        ? Immutable.Map({ key, value: newValue.get("value"), type })
        : Immutable.Map({ value: newValue.get("value"), type });
    })
    .toList();
}

export function transformHTTP(message) {
  const isXML = message.get("requestPayloadType") === "data";
  const headerKeys = message.get("headersList").map(item => item.get("key"));
  const isGetRequest = message.get("requestType") === "GET";
  const transformed = {
    requestPayload: "",
    headers: Immutable.Map(
      headerKeys.zip(message.get("headersList").map(item => item.get("value")))
    ).filter((value, key) => key.length)
  };

  if (!isGetRequest) {
    const payload = message.get("requestPayload");
    let requestPayload;

    if (isXML) {
      requestPayload = payload;
    } else {
      const convertedPayloadMap = convertListToMapDeeply(payload);
      const withSpecialKeys = addSpecialKeysToObj(convertedPayloadMap.toJS());
      const convertedPayloadStr = JSON.stringify(withSpecialKeys);
      requestPayload = convertedPayloadStr;
    }

    Object.assign(transformed, { requestPayload });
  }

  return transformed;
}

export function transformWidget(message) {
  const inputsDataKeys = message.get("inputsDataList").map(item => item.get("key"));
  const transformed = {
    inputsData: Immutable.Map(
      inputsDataKeys.zip(message.get("inputsDataList").map(item => item.get("value")))
    ).filter((value, key) => key.length)
  };

  return transformed;
}

/**
 * Transform an individual language message map before sending it to our API
 *
 * @param {Immutable.Map} messages
 * @returns {Immutable.Map} Transformed messages
 */
function messagesToAPI(messages) {
  return messages.map((message) => {
    let newMessage = message;

    switch (message.type) {
      case "http_request_recipe":
        return message.merge(transformHTTP(message));
      case "widget":
        return message.merge(transformWidget(message));
      case "scheduled_block":
        newMessage = newMessage.updateIn(["affirmativeMessages"], m => messagesToAPI(m));
        newMessage = newMessage.updateIn(["negativeMessages"], m => messagesToAPI(m));

        return newMessage;
      case "conditionals_block":
        newMessage.get("statements").forEach((statementBlock, statementIndex) => {
          newMessage = newMessage.updateIn(["statements", statementIndex, "messages"], m => messagesToAPI(m));
        });

        return newMessage;

      case "salesforce_live_agent": {
        // remove any "empty" variables from customVariableMapping before saving
        const trimmedVariables = message.get("customVariableMapping").filter(
          customVariable => customVariable.get("salesforceFieldName") !== ""
        );

        return message.set("customVariableMapping", trimmedVariables);
      }

      case "unsupported":
        return message.originalMessage;

      default:
        return message;
    }
  });
}

/**
 * Transform multi-language messages map before sending it to our API
 *
 * @param {Immutable.Map} messages - {"en": Immutable.Map}
 * @returns {Immutable.Map} transformed messages
 */
export function toAPI(messages) {
  return messages.map(messagesToAPI);
}
